import React, { useState, useEffect, useCallback } from 'react'
import { api, formatCurrency, formatDateTime } from '../../utils/api'
import { buildAuditCsv } from './auditCsv'

// Max page size the audit-log endpoint accepts — used to walk the whole filtered
// set for the CSV export in as few round-trips as possible.
const EXPORT_PAGE_SIZE = 200

// Read-only pricing-event audit log (spec §6), backed by the real
// /api/pricing/audit-log endpoint (shopify_price_change). Newest first,
// paginated, filterable by product id + date.

const EVENT_STYLES = {
  reprice:           { bg: 'rgba(34, 197, 94, 0.12)',  color: '#16a34a', label: 'Reprice' },
  compare_at_change: { bg: 'rgba(239, 68, 68, 0.12)',  color: '#ef4444', label: 'Compare-at' },
  return_to_curve:   { bg: 'rgba(168, 85, 247, 0.15)', color: '#a855f7', label: 'Return to curve' },
  observed:          { bg: 'rgba(107, 114, 128, 0.18)', color: '#6b7280', label: 'Observed' },
}

const PAGE_SIZE = 50

function formatPence(cents) {
  if (cents == null) return <span style={{ color: '#6b7280', fontSize: 11 }}>&mdash;</span>
  return formatCurrency(cents / 100)
}

// Trigger a browser download of the CSV text. BOM so Excel reads UTF-8.
function downloadCsv(filename, csv) {
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

function eventBadge(eventType) {
  const s = EVENT_STYLES[eventType] || { bg: 'rgba(107, 114, 128, 0.15)', color: '#a0a0c0', label: eventType }
  return (
    <span style={{ background: s.bg, color: s.color, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 500 }}>
      {s.label}
    </span>
  )
}

function change(fromCents, toCents) {
  if (fromCents == null && toCents == null) return <span style={{ color: '#6b7280', fontSize: 11 }}>&mdash;</span>
  const down = fromCents != null && toCents != null && toCents < fromCents
  return (
    <span>
      <span style={{ color: '#6b7280' }}>{formatPence(fromCents)}</span>
      <span style={{ color: '#6b7280', margin: '0 4px' }}>→</span>
      <span style={{ color: down ? '#ef4444' : '#22c55e', fontWeight: 600 }}>{formatPence(toCents)}</span>
    </span>
  )
}

function PricingAuditLog() {
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [exporting, setExporting] = useState(false)

  // Filters apply SERVER-SIDE on an explicit Apply (not live): productId etc.
  // are the *staged* inputs; `applied` is the snapshot the query runs against.
  const [productId, setProductId] = useState('')
  const [eventType, setEventType] = useState('')
  const [actor, setActor] = useState('')
  const [actors, setActors] = useState([])
  const [createdFrom, setCreatedFrom] = useState('')
  const [createdTo, setCreatedTo] = useState('')

  // The applied filter snapshot the query reads. `applyFilters` copies the
  // staged inputs here; until then, typing/selecting doesn't fire a request.
  const [applied, setApplied] = useState({
    productId: '',
    eventType: '',
    actor: '',
    createdFrom: '',
    createdTo: '',
  })

  const buildQuery = useCallback((nextOffset, limit = PAGE_SIZE) => {
    const params = new URLSearchParams()
    params.set('limit', String(limit))
    params.set('offset', String(nextOffset))
    if (applied.productId) params.set('shopify_product_id', applied.productId)
    if (applied.eventType) params.set('event_type', applied.eventType)
    if (applied.actor) params.set('actor', applied.actor)
    if (applied.createdFrom) params.set('created_from', applied.createdFrom)
    if (applied.createdTo) params.set('created_to', applied.createdTo)
    return params.toString()
  }, [applied])

  const loadPage = useCallback(async (nextOffset) => {
    setLoading(true)
    setError(null)
    try {
      const resp = await api.get(`/api/pricing/audit-log?${buildQuery(nextOffset)}`)
      setItems(resp.items || [])
      setTotal(resp.total || 0)
      setActors(resp.actors || [])
      setOffset(nextOffset)
    } catch (e) {
      setError(e.message || 'Failed to load the audit log')
    } finally {
      setLoading(false)
    }
  }, [buildQuery])

  // Export the WHOLE filtered set (not just the page) to CSV. Walks the endpoint
  // at max page size with the currently-applied filters, then downloads.
  async function exportCsv() {
    setExporting(true)
    setError(null)
    try {
      const all = []
      let off = 0
      // Cap the walk defensively; total is authoritative for the stop condition.
      for (let guard = 0; guard < 1000; guard++) {
        const resp = await api.get(`/api/pricing/audit-log?${buildQuery(off, EXPORT_PAGE_SIZE)}`)
        const rows = resp.items || []
        all.push(...rows)
        off += EXPORT_PAGE_SIZE
        if (rows.length < EXPORT_PAGE_SIZE || all.length >= (resp.total || 0)) break
      }
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
      downloadCsv(`pricing-audit-log-${stamp}.csv`, buildAuditCsv(all))
    } catch (e) {
      setError(e.message || 'Export failed')
    } finally {
      setExporting(false)
    }
  }

  // Apply the staged inputs → triggers a reload (loadPage's identity tracks
  // `applied` via buildQuery). A new object each click, so Apply always re-runs.
  function applyFilters() {
    setApplied({
      productId: productId.trim(),
      eventType,
      actor,
      createdFrom,
      createdTo,
    })
  }

  function clearFilters() {
    setProductId('')
    setEventType('')
    setActor('')
    setCreatedFrom('')
    setCreatedTo('')
    setApplied({ productId: '', eventType: '', actor: '', createdFrom: '', createdTo: '' })
  }

  // True when the staged inputs differ from what's applied — highlights Apply.
  const hasPendingChanges =
    productId.trim() !== applied.productId ||
    eventType !== applied.eventType ||
    actor !== applied.actor ||
    createdFrom !== applied.createdFrom ||
    createdTo !== applied.createdTo

  useEffect(() => { loadPage(0) }, [loadPage])

  const page = Math.floor(offset / PAGE_SIZE) + 1
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18 }}>Pricing Audit Log</h2>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: 13 }}>
            Every manual reprice, automated curve-engine reprice, compare-at edit, return-to-curve, and observed out-of-band Shopify change — who, what, from → to, when.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-secondary"
          onClick={exportCsv}
          disabled={exporting || loading || total === 0}
          title="Download the full filtered list as CSV (When, Shopify Product Id, Title, From/To Price, By)"
        >
          {exporting ? 'Exporting…' : `Export CSV${total ? ` (${total})` : ''}`}
        </button>
      </div>

      {/* Filters — house design system (.card → .filter-section → .filter-group) */}
      <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
        <div className="filter-section">
          <div className="filter-group filter-group-wide">
            <label className="filter-label">Shopify Product ID</label>
            <input
              type="text"
              className="form-input"
              placeholder="Filter by product ID..."
              value={productId}
              onChange={e => setProductId(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') applyFilters() }}
            />
          </div>
          <div className="filter-group">
            <label className="filter-label">Event Type</label>
            <select className="form-input" value={eventType} onChange={e => setEventType(e.target.value)}>
              <option value="">All event types</option>
              {Object.entries(EVENT_STYLES).map(([value, s]) => (
                <option key={value} value={value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">By</label>
            <select className="form-input" value={actor} onChange={e => setActor(e.target.value)}>
              <option value="">Anyone</option>
              {actors.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label className="filter-label">Created From</label>
            <input type="date" className="form-input" value={createdFrom} onChange={e => setCreatedFrom(e.target.value)} />
          </div>
          <div className="filter-group">
            <label className="filter-label">Created To</label>
            <input type="date" className="form-input" value={createdTo} onChange={e => setCreatedTo(e.target.value)} />
          </div>
        </div>

        <div className="filter-actions" style={{ justifyContent: 'flex-start', gap: 'var(--space-3)', marginTop: 'var(--space-3)' }}>
          <button
            type="button"
            className={`btn btn-sm ${hasPendingChanges ? 'btn-primary' : 'btn-secondary'}`}
            onClick={applyFilters}
            disabled={loading}
          >
            {loading ? 'Loading…' : 'Apply filters'}
          </button>
          <button type="button" className="btn btn-sm btn-secondary" onClick={clearFilters} disabled={loading}>
            Clear
          </button>
          {hasPendingChanges && (
            <span className="text-sm text-muted" style={{ alignSelf: 'center' }}>Unapplied changes</span>
          )}
        </div>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border-color)', color: '#6b7280', fontSize: 12 }}>
          {total} {total === 1 ? 'entry' : 'entries'}
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table className="table" style={{ fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ width: 150 }}>When</th>
                <th>Product</th>
                <th style={{ width: 130 }}>Event</th>
                <th style={{ width: 180 }}>Price</th>
                <th style={{ width: 180 }}>Compare-at</th>
                <th style={{ width: 110 }}>By</th>
              </tr>
            </thead>
            <tbody>
              {items.map(e => (
                <tr key={e.id}>
                  <td style={{ color: '#6b7280', fontSize: 11 }}>{formatDateTime(e.created_at)}</td>
                  <td>
                    <div style={{ fontWeight: 500 }}>{e.title || <span style={{ color: '#6b7280' }}>(unknown)</span>}</div>
                    <div className="text-sm text-muted" style={{ fontFamily: 'monospace', fontSize: 10 }}>{e.shopify_product_id}</div>
                  </td>
                  <td>{eventBadge(e.event_type)}</td>
                  <td>{change(e.old_price_cents, e.new_price_cents)}</td>
                  <td>{change(e.old_compare_at_cents, e.new_compare_at_cents)}</td>
                  <td style={{ color: e.intent_source === 'observed' ? '#6b7280' : undefined }}>
                    {e.intent_source === 'engine' ? (
                      // Automated curve-engine push (actor=curve_engine): make
                      // "system did this" unmissable, distinct from operator names.
                      <span style={{ color: '#3b82f6', fontWeight: 600 }}>⚙ Curve engine</span>
                    ) : e.actor}
                    {e.intent_source === 'observed' && <div style={{ color: '#9ca3af', fontSize: 10 }}>auto</div>}
                    {e.intent_source === 'engine' && <div style={{ color: '#9ca3af', fontSize: 10 }}>automated</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading…</div>}
        {error && <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>{error}</div>}
        {!loading && !error && items.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>No pricing events match your filters.</div>
        )}

        {pages > 1 && (
          <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border-color)', display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end' }}>
            <button className="btn btn-sm btn-secondary" disabled={offset === 0 || loading} onClick={() => loadPage(Math.max(0, offset - PAGE_SIZE))}>Prev</button>
            <span style={{ color: '#6b7280', fontSize: 12 }}>Page {page} / {pages}</span>
            <button className="btn btn-sm btn-secondary" disabled={page >= pages || loading} onClick={() => loadPage(offset + PAGE_SIZE)}>Next</button>
          </div>
        )}
      </div>
    </div>
  )
}

export default PricingAuditLog
